import { useEffect, useState } from 'react'
import { EmptyFolder, LoadFilesError } from '@/assets/illustration'
import { Retry } from '@/assets/icons'
import Breadcrumbs from '@/components/breadcrumbs/Breadcrumbs'
import { Button } from '@/components/button/Button'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'
import type { BreadcrumbItemType } from '@/components/breadcrumbs/BreadcrumbItem'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import type { FolderResponse } from '@/user_files/folders/schemas'
import { InitialExternalSyncStatus } from '@/user_files/external-storage/components/InitialExternalSyncStatus'
import { SearchEmptyState } from '@/user_files/search/components/SearchEmptyState'
import { MatterFilesFolderFileList, type MatterFilesFileToggleOptions } from './MatterFilesFolderFileList'
import type { FolderSelectionState } from './useRecursiveFolderSelection'

const SKELETON_MIN_MS = 300

export type BrowseMatterFilesProps = {
  /** Breadcrumb trail for the current browse path within the matter. */
  breadcrumbItems: BreadcrumbItemType[]
  /** Child folders of the browsed folder. */
  folders: FolderResponse[]
  /** Matter root used to identify retained top-level synced folders in the file picker. */
  matterRootFolderId?: string | null
  /** Files in the browsed folder. */
  files: UserFileRead[]
  /** True while the browsed folder’s data is not ready to show the list. */
  loading: boolean
  /** Provider name shown while its first sync has not produced any visible children. */
  initialSyncSourceLabel?: string
  /** Blocks the file list with empty error UI (illustration + optional Retry), e.g. browse / folder fetch failed. */
  blockingError?: string
  /** Refetch after a blocking browse load failure (`blockingError`). */
  onRetry?: () => void
  /** Optional empty state override. */
  emptyState?: {
    title: string
    description?: string
    action?: {
      label: string
      onClick: () => void
    }
  }
  /** When true, folder row buttons are disabled (e.g. move mutation in flight). File rows unchanged. */
  disabled?: boolean
  /** File ids selected for add-files (checkboxes when `onToggleFile` is set). */
  selectedFileIds?: string[]
  /** When set, file rows are selectable. */
  onToggleFile?: (file: UserFileRead, options?: MatterFilesFileToggleOptions) => void
  /** Controls the input type for selectable rows. Defaults to 'checkbox'. */
  selectionVariant?: 'checkbox' | 'radio'
  /** Optional file-level selection rule. Defaults to allowing every loaded file. */
  isFileSelectable?: (file: UserFileRead) => boolean
  includeProcessingFiles?: boolean
  /** Optional detected-format compatibility rule. Rejections use the standard unsupported-format tooltip. */
  isFileFormatSupported?: (file: UserFileRead) => boolean
  /** Optional tooltip shown for files rejected by `isFileSelectable`. */
  fileNotSelectableMessage?: string | ((file: UserFileRead) => string | undefined)
  /** Get recursive selection state for a child folder row. */
  getFolderSelectionState?: (folderId: string) => FolderSelectionState
  /** Get tooltip copy for a child folder's recursive selection state. */
  getFolderSelectionTooltipMessage?: (folderId: string) => string | null
  /** Toggle recursive file selection for a child folder row. */
  onToggleFolderSelection?: (folder: FolderResponse) => void
  /** Open a subfolder by id. */
  onNavigateToFolder: (folderId: string) => void
  /**
   * Present while the list shows matter-wide search results instead of the browsed folder. The
   * breadcrumb bar names the query instead of the path, and an empty list means "nothing matched"
   * rather than "this folder is empty".
   */
  searchResults?: BrowseMatterFilesSearchResults
}

export type BrowseMatterFilesSearchResults = {
  /** The query the results belong to, rendered in the header and the empty state. */
  query: string
  /** Full location per result id, rendered as each row's second line. */
  locationPathById: ReadonlyMap<string, string>
  onClearSearch: () => void
}

export function BrowseMatterFiles({
  breadcrumbItems,
  disabled = false,
  files,
  folders,
  matterRootFolderId,
  blockingError,
  loading,
  selectedFileIds,
  onNavigateToFolder,
  onRetry,
  onToggleFile,
  onToggleFolderSelection,
  selectionVariant,
  isFileSelectable,
  includeProcessingFiles,
  isFileFormatSupported,
  fileNotSelectableMessage,
  emptyState,
  getFolderSelectionState,
  getFolderSelectionTooltipMessage,
  initialSyncSourceLabel,
  searchResults,
}: BrowseMatterFilesProps) {
  const [showSkeleton, setShowSkeleton] = useState(false)

  // Set how long we want to dwell on the skeleton (to avoid flickering when loading).
  useEffect(() => {
    if (loading) {
      setShowSkeleton(true)
      return
    }
    const timer = setTimeout(() => setShowSkeleton(false), SKELETON_MIN_MS)
    return () => clearTimeout(timer)
  }, [loading])

  const isEmpty = folders.length === 0 && files.length === 0
  const showInitialSyncState = Boolean(initialSyncSourceLabel && isEmpty && !blockingError)

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex min-h-12 shrink-0 items-center border-b border-brand-neutral-200 bg-brand-neutral-50 px-6 py-1.5">
        {searchResults ? (
          // Results span the whole matter, so there is no path to show — the query is what the
          // list is scoped to.
          <p className="min-w-0 flex-1 truncate text-sm leading-5 text-brand-neutral-600">
            Search results for <span className="font-semibold text-brand-neutral-950">{searchResults.query}</span>
          </p>
        ) : (
          <div className="-ml-1 min-w-0 flex-1">
            <Breadcrumbs items={breadcrumbItems} maxItems={3} persistFirstItem />
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {showInitialSyncState && initialSyncSourceLabel ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain" aria-busy="true">
            <div className="h-12 shrink-0 border-b border-brand-neutral-200 px-6">
              <InitialExternalSyncStatus sourceLabel={initialSyncSourceLabel} description="Files will appear as they import." />
            </div>
            <ul className="m-0 list-none p-0">
              {Array.from({ length: 5 }, (_, i) => (
                <li key={i} className="flex h-12 items-center gap-2.5 border-b border-brand-neutral-200 px-4">
                  <LoadingSkeleton width={56} lineHeight={12} />
                  <span className="min-w-0 flex-1">
                    <LoadingSkeleton width="100%" lineHeight={12} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : showSkeleton ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain" aria-busy="true">
            <ul className="m-0 list-none divide-y divide-brand-neutral-200 p-0">
              {Array.from({ length: 5 }, (_, i) => (
                <li key={i} className="flex h-12 items-center pl-6 pr-3">
                  <LoadingSkeleton width="100%" />
                </li>
              ))}
            </ul>
          </div>
        ) : blockingError ? (
          <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center gap-4 px-6 pb-6">
            <LoadFilesError className="h-10 w-auto shrink-0 text-true-gray-300" aria-hidden />
            <div className="flex max-w-[292px] flex-col items-center gap-0.5">
              <p className="text-center text-sm font-medium leading-5 tracking-tight text-red-800">{blockingError}</p>
              {onRetry ? (
                <Button
                  type="button"
                  variant="plain"
                  color="secondary"
                  size="sm"
                  trailingIcon={<Retry className="h-4 w-4 shrink-0" aria-hidden />}
                  onClick={onRetry}
                >
                  Retry
                </Button>
              ) : null}
            </div>
          </div>
        ) : isEmpty && searchResults ? (
          <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center px-6 pb-6">
            <SearchEmptyState query={searchResults.query} onClearSearch={searchResults.onClearSearch} />
          </div>
        ) : isEmpty ? (
          <div className="flex min-h-[200px] flex-1 flex-col items-center justify-center gap-4 px-6 pb-6">
            <EmptyFolder className="h-10 w-auto shrink-0" aria-hidden />
            {emptyState ? (
              <div className="flex max-w-[348px] flex-col items-center gap-1.5 text-center">
                <p className="text-base font-medium leading-6 text-brand-neutral-950">{emptyState.title}</p>
                {emptyState.description ? <p className="text-sm font-medium leading-5 text-brand-neutral-600">{emptyState.description}</p> : null}
                {emptyState.action ? (
                  <Button type="button" variant="plain" color="primary" size="sm" onClick={emptyState.action.onClick}>
                    {emptyState.action.label}
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="max-w-[292px] text-center text-sm font-medium leading-5 text-brand-neutral-600">There are no subfolders or files in this folder</p>
            )}
          </div>
        ) : (
          <MatterFilesFolderFileList
            includeProcessingFiles={includeProcessingFiles}
            disabled={disabled}
            files={files}
            folders={folders}
            matterRootFolderId={matterRootFolderId}
            getFolderSelectionState={getFolderSelectionState}
            getFolderSelectionTooltipMessage={getFolderSelectionTooltipMessage}
            onNavigateToFolder={onNavigateToFolder}
            onToggleFile={onToggleFile}
            onToggleFolderSelection={onToggleFolderSelection}
            selectedFileIds={selectedFileIds}
            selectionVariant={selectionVariant}
            isFileSelectable={isFileSelectable}
            isFileFormatSupported={isFileFormatSupported}
            fileNotSelectableMessage={fileNotSelectableMessage}
            locationPathById={searchResults?.locationPathById}
            searchQuery={searchResults?.query}
          />
        )}
      </div>
    </div>
  )
}
